// Wants Board web API client (ADR-0011). GET-only; login required — the
// server returns 401 for anonymous callers and 403 for non-members of a
// group scope.

import { API_ENDPOINTS } from './endpoints.js';
import { apiResult } from './api-fetch.js';
import { buildBoardQuery } from '../logic/wants-board-logic.js';

/**
 * Fetch one section page of the board.
 * @param {import('../types.js').BoardQueryParams} params
 * @returns {Promise<import('../types.js').ApiResult<import('../types.js').BoardSectionPage>>}
 */
export async function fetchBoardSection(params) {
  const qs = buildBoardQuery(params);
  return apiResult(`${API_ENDPOINTS.wants_board}?${qs}`);
}

/**
 * Fetch the full wanter/holder breakdown for one card name.
 * @param {{scope: string, groupId?: string, subgroupIds?: string, cardName: string}} params
 * @returns {Promise<import('../types.js').ApiResult<import('../types.js').BoardDetail>>}
 */
export async function fetchBoardDetail({ scope, groupId, subgroupIds, cardName }) {
  const sp = new URLSearchParams();
  sp.set('scope', scope);
  if (groupId) sp.set('groupId', groupId);
  if (subgroupIds) sp.set('subgroupIds', subgroupIds);
  sp.set('detail', cardName);
  let url = `${API_ENDPOINTS.wants_board}?${sp.toString()}`;
  // Re-encode the detail param to use %20 instead of +
  url = url.replace(/detail=[^&]*/, `detail=${encodeURIComponent(cardName)}`);
  return apiResult(url);
}

/**
 * The viewer's Want Overlap with the viewed binder's sole claimant (spec
 * 2026-09-21). Counts only. The server answers the empty shape for every
 * gated case, so the caller needs no error branch beyond `ok`.
 * @param {string} binderUrl short form, e.g. '/mfb.abc'
 * @returns {Promise<import('../types.js').ApiResult<import('../types.js').WantOverlap>>}
 */
export async function fetchWantOverlap(binderUrl) {
  const sp = new URLSearchParams();
  sp.set('overlap', binderUrl);
  return apiResult(`${API_ENDPOINTS.wants_board}?${sp.toString()}`);
}

/**
 * The printings in one binder that the viewer wants (`?hits=`): the in-binder
 * star's filled state. The binder page itself carries no Viewer data, so this
 * is the one place the star learns it. Beside the overlap: the other
 * per-binder Viewer read on this function.
 * @param {string} binderUrl short form, e.g. '/mfb.abc'
 * @returns {Promise<import('../types.js').ApiResult<{ hits: import('../types.js').WantHit[] }>>}
 */
export async function fetchWantHits(binderUrl) {
  const sp = new URLSearchParams();
  sp.set('hits', binderUrl);
  return apiResult(`${API_ENDPOINTS.wants_board}?${sp.toString()}`);
}
